/**
 * Configuration Validation
 * Runtime validation with descriptive error messages
 */

import type { AppPublicConfig, AppServerConfig, RefreshLimits } from "./types";
import { isValidEnvironment, isValidUrl, isNonNegativeInteger } from "./schema";

/**
 * Validate refresh limits object
 */
function validateRefreshLimits(limits: unknown): RefreshLimits {
  if (!limits || typeof limits !== "object") {
    throw new Error("refreshLimits must be an object");
  }

  const obj = limits as Record<string, unknown>;

  const maxAuto = obj.maxAutoRefreshPerMinute;
  if (!isNonNegativeInteger(maxAuto)) {
    throw new Error("refreshLimits.maxAutoRefreshPerMinute must be a non-negative integer");
  }

  const maxManual = obj.maxManualRefreshPerMinute;
  if (!isNonNegativeInteger(maxManual)) {
    throw new Error("refreshLimits.maxManualRefreshPerMinute must be a non-negative integer");
  }

  const minSeconds = obj.minSecondsBetweenRefreshSameCell;
  if (!isNonNegativeInteger(minSeconds)) {
    throw new Error("refreshLimits.minSecondsBetweenRefreshSameCell must be a non-negative integer");
  }

  return {
    maxAutoRefreshPerMinute: maxAuto,
    maxManualRefreshPerMinute: maxManual,
    minSecondsBetweenRefreshSameCell: minSeconds,
  };
}

/**
 * Validate public configuration inner object
 */
function validatePublicConfigInner(publicConfig: unknown): AppPublicConfig["public"] {
  if (!publicConfig || typeof publicConfig !== "object") {
    throw new Error("public must be an object");
  }

  const obj = publicConfig as Record<string, unknown>;

  const apiBaseUrl = obj.apiBaseUrl;
  if (typeof apiBaseUrl !== "string" || !apiBaseUrl.trim()) {
    throw new Error("public.apiBaseUrl must be a non-empty string");
  }
  if (!isValidUrl(apiBaseUrl)) {
    throw new Error("public.apiBaseUrl must be a valid http:// or https:// URL");
  }

  const featureFlags = obj.featureFlags;
  if (featureFlags !== undefined && featureFlags !== null) {
    if (typeof featureFlags !== "object" || Array.isArray(featureFlags)) {
      throw new Error("public.featureFlags must be an object");
    }
    const flags = featureFlags as Record<string, unknown>;
    for (const [key, value] of Object.entries(flags)) {
      if (typeof value !== "boolean") {
        throw new Error("public.featureFlags." + key + " must be a boolean");
      }
    }
  }

  const refreshLimits = validateRefreshLimits(obj.refreshLimits);

  return {
    apiBaseUrl: apiBaseUrl.trim(),
    featureFlags: (featureFlags as Record<string, boolean>) || {},
    refreshLimits,
  };
}

/**
 * Validate AppPublicConfig (canonical client shape)
 */
export function validateAppPublicConfig(config: unknown): AppPublicConfig {
  if (!config || typeof config !== "object") {
    throw new Error("config must be an object");
  }

  const obj = config as Record<string, unknown>;

  const env = obj.env;
  if (typeof env !== "string") {
    throw new Error("env must be a string");
  }
  if (!isValidEnvironment(env)) {
    throw new Error("env must be one of: dev, stage, prod");
  }

  const publicConfig = validatePublicConfigInner(obj.public);

  return {
    env,
    public: publicConfig,
  };
}

/**
 * Validate AppServerConfig (canonical server shape)
 */
export function validateAppServerConfig(config: unknown): AppServerConfig {
  // Validate public portion first
  const publicConfig = validateAppPublicConfig(config);

  const obj = config as Record<string, unknown>;

  // Validate private portion
  const privateConfig = obj.private;
  if (privateConfig !== undefined && privateConfig !== null) {
    if (typeof privateConfig !== "object" || Array.isArray(privateConfig)) {
      throw new Error("private must be an object");
    }
  }

  const privateObj = (privateConfig as Record<string, unknown>) || {};

  return {
    env: publicConfig.env,
    public: publicConfig.public,
    private: {
      massiveApiKey: typeof privateObj.massiveApiKey === "string" ? privateObj.massiveApiKey : undefined,
    },
  };
}
